import { Amplify } from 'aws-amplify';

export interface AmplifyConfig {
  environment: 'sandbox' | 'production';
  region: string;
  userPoolId: string;
  userPoolClientId?: string;
  graphqlEndpoint: string;
}

let isConfigured = false;

const looksConfiguredFromAmplify = (): boolean => {
  // Bridge bundles may be hosted inside a React app shell that already called Amplify.configure(outputs).
  // In that case, we should treat Amplify as configured even if the bridge didn't run configureAmplify().
  try {
    const getConfig = (Amplify as unknown as { getConfig?: () => unknown }).getConfig;
    if (typeof getConfig !== 'function') {
      return false;
    }
    const originalWarn = console.warn;
    let cfg: any;
    try {
      // Amplify logs a warning when `getConfig()` is called before `Amplify.configure()`.
      // We call it only as a probe; keep the console clean for verification workflows.
      console.warn = (...args: unknown[]) => {
        const first = typeof args[0] === 'string' ? args[0] : '';
        if (first.includes('Amplify has not been configured')) {
          return;
        }
        originalWarn(...args);
      };
      cfg = getConfig() as any;
    } finally {
      console.warn = originalWarn;
    }
    const endpoint = cfg?.API?.GraphQL?.endpoint;
    const region = cfg?.API?.GraphQL?.region;
    const userPoolId = cfg?.Auth?.Cognito?.userPoolId;
    return Boolean(endpoint && region && userPoolId);
  } catch {
    return false;
  }
};

export function configureAmplify(config: AmplifyConfig): void {
  if (isConfigured) {
    return;
  }
  if (looksConfiguredFromAmplify()) {
    // Adopt host-app configuration silently. This is an expected bootstrap path
    // when the shell configured Amplify before the bridge installs.
    isConfigured = true;
    return;
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId,
        userPoolClientId: config.userPoolClientId ?? `${config.userPoolId}_client`,
        signUpVerificationMethod: 'code',
        loginWith: {
          oauth: {
            domain: `auth.${config.environment === 'production' ? '' : 'sandbox.'}triedge.io`,
            scopes: ['openid', 'email', 'profile'],
            redirectSignIn: [origin],
            redirectSignOut: [origin],
            responseType: 'code'
          }
        }
      }
    },
    API: {
      GraphQL: {
        endpoint: config.graphqlEndpoint,
        region: config.region,
        defaultAuthMode: 'userPool'
      }
    }
  });

  isConfigured = true;
}

export function isAmplifyConfigured(): boolean {
  return isConfigured || looksConfiguredFromAmplify();
}

export function getAmplifyClient(): typeof Amplify {
  if (!isAmplifyConfigured()) {
    throw new Error('[TriedgeBridge] Amplify not configured. Call configureAmplify() first.');
  }
  // Ensure any direct "isConfigured" checks stay consistent once we detect host configuration.
  isConfigured = true;
  return Amplify;
}
